import { Link } from "react-router-dom";
import { Clock, FileText, AlertTriangle, MapPin, CheckCircle, Info } from "lucide-react";

// Patterns to auto-link (no inline icons in text)
const linkPatterns: { pattern: RegExp; href: string; target?: string; internal?: boolean }[] = [
  { pattern: /01 49 88 92 40/g, href: "tel:0149889240" },
  { pattern: /autosur\.montreuil@gmail\.com/g, href: "mailto:autosur.montreuil@gmail.com" },
  { pattern: /info-ct@utac\.com/g, href: "mailto:info-ct@utac.com" },
  { pattern: /133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois/g, href: "https://maps.app.goo.gl/QMbfid7TsPVr8ZB76", target: "_blank" },
  { pattern: /voiture \(VL\)/g, href: "/vehicules/voitures", internal: true },
  { pattern: /moto \/ scooter \/ quad \/ voiturette/g, href: "/vehicules/motos", internal: true },
  { pattern: /prenez rendez-vous en ligne/g, href: "/contact", internal: true },
];

// Split text on explicit \n\n into paragraphs
const splitIntoParagraphs = (text: string): string[] => {
  // First split on double newlines
  const blocks = text.split(/\n\n/);
  
  if (blocks.length > 1) return blocks.map(b => b.trim()).filter(Boolean);

  // Fallback: split on ". " followed by a letter (preserves emails like info@utac.com)
  const parts = text.split(/(?<=\.)\s+(?=[A-ZÀ-ÿ])/);
  
  if (parts.length <= 2) return [text];
  
  const paragraphs: string[] = [];
  for (let i = 0; i < parts.length; i += 2) {
    const chunk = parts.slice(i, i + 2).join(" ");
    paragraphs.push(chunk.trim());
  }
  
  return paragraphs;
};

// Render text with auto-linked patterns (no icons, just styled links)
const renderWithLinks = (text: string): (string | JSX.Element)[] => {
  const allPatterns = linkPatterns.map(p => p.pattern.source).join("|");
  const combinedRegex = new RegExp(`(${allPatterns})`, "g");
  
  const parts = text.split(combinedRegex);
  
  return parts.map((part, i) => {
    if (!part) return null;
    
    for (const { pattern, href, target, internal } of linkPatterns) {
      const freshPattern = new RegExp(pattern.source, pattern.flags);
      if (freshPattern.test(part)) {
        if (internal) {
          return (
            <Link
              key={i}
              to={href}
              className="text-autosur-blue hover:underline font-semibold"
            >
              {part}
            </Link>
          );
        }
        return (
          <a
            key={i}
            href={href}
            target={target}
            rel={target ? "noopener noreferrer" : undefined}
            className="text-autosur-blue hover:underline font-semibold"
          >
            {part}
          </a>
        );
      }
    }
    
    return <span key={i}>{part}</span>;
  }).filter(Boolean) as (string | JSX.Element)[];
};

// Render a paragraph, handling bullet lines (•) as a list
const renderParagraph = (para: string, idx: number) => {
  const lines = para.split("\n");
  const bulletLines = lines.filter(l => l.trim().startsWith("•"));
  
  if (bulletLines.length > 0) {
    const nonBullets = lines.filter(l => !l.trim().startsWith("•"));
    return (
      <div key={idx}>
        {nonBullets.length > 0 && nonBullets[0].trim() && (
          <p className="font-body text-muted-foreground leading-relaxed mb-2">
            {renderWithLinks(nonBullets[0])}
          </p>
        )}
        <ul className="space-y-1.5 ml-1">
          {bulletLines.map((line, j) => (
            <li key={j} className="font-body text-muted-foreground leading-relaxed flex items-start gap-2">
              <span className="text-autosur-blue font-bold mt-0.5">•</span>
              <span>{renderWithLinks(line.replace(/^•\s*/, ""))}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // Check if entire paragraph is italic (_text_)
  const italicMatch = para.match(/^_(.+)_$/);
  if (italicMatch) {
    return (
      <p key={idx} className="font-body text-muted-foreground/70 leading-relaxed text-sm italic">
        {renderWithLinks(italicMatch[1])}
      </p>
    );
  }

  return (
    <p key={idx} className="font-body text-muted-foreground leading-relaxed">
      {renderWithLinks(para)}
    </p>
  );
};

// Detect contextual icon for the answer (shown once at start, not inline)
const getAnswerIcon = (question: string): JSX.Element | null => {
  const q = question.toLowerCase();
  if (q.includes("horaire") || q.includes("validité") || q.includes("durée") || q.includes("quand") || q.includes("échéance") || q.includes("combien de temps")) return <Clock className="w-5 h-5 text-autosur-blue flex-shrink-0 mt-0.5" />;
  if (q.includes("document") || q.includes("carte grise") || q.includes("procès-verbal") || q.includes("duplicata") || q.includes("attestation")) return <FileText className="w-5 h-5 text-autosur-blue flex-shrink-0 mt-0.5" />;
  if (q.includes("risque") || q.includes("sans contrôle") || q.includes("amende")) return <AlertTriangle className="w-5 h-5 text-autosur-red flex-shrink-0 mt-0.5" />;
  if (q.includes("où faire") || q.includes("adresse") || q.includes("situé")) return <MapPin className="w-5 h-5 text-autosur-blue flex-shrink-0 mt-0.5" />;
  if (q.includes("possible") || q.includes("peut-on") || q.includes("existe")) return <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />;
  return <Info className="w-5 h-5 text-autosur-blue/60 flex-shrink-0 mt-0.5" />;
};

interface FAQAnswerProps {
  answer: string;
  question: string;
}

export const FAQAnswer = ({ answer, question }: FAQAnswerProps) => {
  const paragraphs = splitIntoParagraphs(answer);
  const icon = getAnswerIcon(question);

  return (
    <div className="flex gap-3">
      {icon && <div className="hidden sm:block">{icon}</div>}
      <div className="space-y-3 flex-1">
        {paragraphs.map((para, i) => renderParagraph(para, i))}
      </div>
    </div>
  );
};
